// Libs
import React, { useState, useContext } from "react";
import { StoresContext, Stores } from "Globals/Stores";
import { useObserver, KeyValuePair, Grid, Else, FieldType } from "@shoothill/core";
import { Link as RouterLink } from "react-router-dom";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { RadioSVG } from "Globals/Views/IconSVGs/RadioSVG";
import { RadioCheckedSVG } from "Globals/Views/IconSVGs/RadioCheckedSVG";
import { Button, Table, TableHead, TableBody, TableRow, TableContainer, TableCell, Box, Checkbox, Typography } from "@material-ui/core";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DateFnsUtils from "@date-io/date-fns";
import styled from "styled-components";
import { BinSVG } from "Globals/Views/IconSVGs/BinSVG";
import { CheckboxCheckedSVG } from "Globals/Views/IconSVGs/CheckboxCheckedSVG";
import { CheckboxSVG } from "Globals/Views/IconSVGs/CheckboxSVG";

// App
import { theme } from "Globals/Styles/AppTheme";
import IncomeDetailsViewModel from "Globals/ViewModels/FormSections/IncomeSavingsAndOutgoings/IncomeDetailsViewModel";
import CreditCardViewModel from "Globals/ViewModels/FormSections/IncomeSavingsAndOutgoings/CreditCardViewModel";
import ApplicationFormViewModel from "Globals/ViewModels/ApplicationFormViewModel";
import { CurrencyInput } from "Globals/Views/CurrencyInput";

// Styles
import { StyledButton } from "Globals/Styles/ButtonStyles";
import { Row } from "Globals/Styles/LayoutStyles";
import { CustomInput } from "Globals/Styles/AppStyling";

export const StyledAddButton = styled(StyledButton)`
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    padding: 0px;
    margin: 0 0 1px 0;
`;
interface IProps {
    incomeDetailsViewModel: IncomeDetailsViewModel;
    applicationFormViewModel: ApplicationFormViewModel;
    isPrimary?: boolean;
}

export const CreditCardView: React.FC<IProps> = (props: IProps) => {
    const storesContext = useContext<Stores>(StoresContext);
    const [upsertViewModel] = useState(() => new CreditCardViewModel());
    return useObserver(() => (
        <>
            {(upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving) == false && (
                <>
                    <Row style={{ alignItems: "center" }}>
                        <CustomInput
                            label="Account holder's name"
                            placeHolder="Enter the account holder's name"
                            viewModel={upsertViewModel}
                            fieldName="acountHolderName"
                            shrink={true}
                            inputProps={{
                                disabled: upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving,
                            }}
                        />
                        <CustomInput
                            type="select"
                            style={{ minWidth: "350px" }}
                            label="Credit card company"
                            viewModel={upsertViewModel}
                            fieldName="lenderId"
                            selectItems={storesContext.Domain.LenderTypeStore.getLenderTypeOptions}
                            inputProps={{
                                disabled: upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving,
                            }}
                        />
                        {upsertViewModel.getModel.getIsLenderOther && (
                            <CustomInput
                                label="Credit card provider"
                                placeHolder="Enter the name of the credit card provider"
                                viewModel={upsertViewModel}
                                fieldName="otherLender"
                                shrink={true}
                                inputProps={{
                                    disabled: upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving,
                                }}
                            />
                        )}

                        <CurrencyInput
                            viewModel={upsertViewModel}
                            label={"Monthly payment"}
                            placeholder={"Enter monthly payment"}
                            fieldName={"monthlyPayment"}
                            disabled={upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving}
                        />
                    </Row>
                    <Row style={{ alignItems: "center" }}>
                        <CurrencyInput
                            viewModel={upsertViewModel}
                            label={"Balance"}
                            placeholder={"Enter balance"}
                            fieldName={"balance"}
                            disabled={upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving}
                        />

                        {props.isPrimary && props.applicationFormViewModel.getSortedApplicantViewModels.length > 1 && (
                            <Box>
                                <Row style={{ alignItems: "center" }}>
                                    <Checkbox
                                        checked={upsertViewModel.getValue("sameForApplicantTwo")}
                                        onChange={(e) => {
                                            upsertViewModel.handleCheckboxChange("sameForApplicantTwo", e.target.checked);
                                        }}
                                        icon={<CheckboxSVG />}
                                        checkedIcon={<CheckboxCheckedSVG />}
                                        disabled={props.applicationFormViewModel.model.isSubmitted}
                                    />
                                    <Typography variant="body1" style={{ fontWeight: "bold" }}>
                                        Same for applicant 2
                                    </Typography>
                                </Row>
                            </Box>
                        )}

                        <Box style={{ display: "flex", alignItems: "start", marginTop: "22px" }}>
                            <StyledAddButton
                                variant="contained"
                                disableElevation
                                onClick={() => upsertViewModel.stash(props.incomeDetailsViewModel)}
                                disabled={upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving}
                            >
                                +
                            </StyledAddButton>
                        </Box>
                    </Row>
                </>
            )}
            {props.incomeDetailsViewModel.getSortedCreditCardViewModels.length > 0 && (
                <TableContainer>
                    <Table style={{ width: "100%" }} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Account holder's name</TableCell>
                                <TableCell align="center">Credit card company</TableCell>
                                <TableCell align="center">Monthly payment</TableCell>
                                <TableCell align="center">Balance</TableCell>
                                {props.isPrimary && <TableCell align="center">Same for applicant 2</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {props.incomeDetailsViewModel.getSortedCreditCardViewModels.map((creditCard) => (
                                <TableRow key={creditCard.model.id}>
                                    <TableCell component="th" scope="creditCard">
                                        {creditCard.model.acountHolderName}
                                    </TableCell>
                                    <TableCell align="center">{storesContext.Domain.LenderTypeStore.getLendersName(creditCard.model.lenderId)}</TableCell>
                                    <TableCell align="center">£{creditCard.model.monthlyPayment}</TableCell>
                                    <TableCell align="center">£{creditCard.model.balance}</TableCell>
                                    {props.isPrimary && <TableCell align="center">{creditCard.getValue("sameForApplicantTwo") ? "Yes" : "No"}</TableCell>}
                                    <TableCell align="center">
                                        {(upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving) == false && (
                                            <Button
                                                onClick={() => {
                                                    creditCard.delete(props.incomeDetailsViewModel);
                                                }}
                                            >
                                                <BinSVG />
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </>
    ));
};
